import { forwardRef } from "react";
import { readableColor, shade, tint } from "polished";
import styled, { css } from "styled-components";

import { Box } from "@vinomofo/components";

interface StyledButtonProps {
  borderColor?: string;
  backgroundColor?: string;
  outline?: boolean;
  isCTA?: boolean;
}

const basicStyle = ({ theme }) => css`
  display: inline-block;
  box-sizing: border-box;
  cursor: pointer;
  font: inherit;
  text-decoration: none;
  margin: 0;
  background: transparent;
  overflow: visible;
  text-transform: none;
  font-family: ${theme.fonts.heading};
  font-size: 14px;
  font-weight: 700;
  border-radius: 100px;
  padding: 10px 18px;
  line-height: none;

  @media ${theme.mq.min.mobile} {
    padding: 0;
    font-size: 16px;
  }
`;

const defaultStyle = ({ theme, borderColor, hoverColor }) => css`
  background-color: ${theme.colors.primary};
  border: 2px solid ${borderColor || theme.colors.primary};
  text-decoration: none;

  && a {
    color: ${theme.colors.white};
  }

  &:hover {
    && {
      color: ${(hoverColor && theme.colors[hoverColor]) || theme.colors.white};
    }

    border-color: ${tint(0.1, theme.colors.greenLantern)};
    background-color: ${tint(0.1, theme.colors.greenLantern)};
  }

  &:active {
    border-color: ${shade(0.1, theme.colors.greenLantern)};
    background-color: ${shade(0.1, theme.colors.greenLantern)};
  }
`;

const outlineStyle = ({
  theme,
  backgroundColor,
  borderColor = theme.colors.primary,
  hoverColor,
}) => css`
  background-color: transparent;
  border: 2px solid ${borderColor};

  && a {
    color: ${borderColor};
  }

  &:hover {
    & a {
      color: ${hoverColor ||
      (borderColor &&
        readableColor(
          theme.colors[borderColor] || borderColor,
          theme.colors.black,
          theme.colors.white
        ))} !important;
    }
    border-color: ${borderColor};
    background-color: ${borderColor};
  }
`;

const ghostStyle = ({ theme, color }) => css`
  background-color: transparent;
  border: 2px solid transparent;

  &:hover {
    && a {
      color: ${color} !important;
    }

    border-color: ${tint(0.9, theme.colors.greenLantern)};
    background-color: ${tint(0.9, theme.colors.greenLantern)};
  }
`;

const disabledStyle = () => css`
  filter: grayscale(100%);
  cursor: not-allowed;
  opacity: 0.5;
`;

const backgroundStyle = ({ theme, borderColor, backgroundColor }) => css`
  background-color: ${theme.colors[backgroundColor] || backgroundColor};
  border: 2px solid
    ${theme.colors[borderColor] ||
    borderColor ||
    theme.colors[backgroundColor] ||
    backgroundColor};

  && a {
    color: ${readableColor(
      theme.colors[backgroundColor] || backgroundColor,
      theme.colors.black,
      theme.colors.white
    )};
  }

  &:hover {
    && a {
      color: ${readableColor(
        theme.colors[backgroundColor] || backgroundColor,
        theme.colors.black,
        theme.colors.white
      )};
    }

    border-color: ${tint(
      0.1,
      theme.colors[backgroundColor] || backgroundColor
    )};
    background-color: ${tint(
      0.1,
      theme.colors[backgroundColor] || backgroundColor
    )};
  }

  &:active {
    border-color: ${shade(
      0.1,
      theme.colors[backgroundColor] || backgroundColor
    )};
    background-color: ${shade(
      0.1,
      theme.colors[backgroundColor] || backgroundColor
    )};
  }
`;

const overrideStyle = ({ theme, color, fontWeight }) => css`
  &&& {
    && a {
      color: ${theme.colors[color] || color};
      font-weight: ${fontWeight};
    }

    &:hover,
    &:active {
      && a {
        color: ${theme.colors[color] || color};
      }
    }
  }
`;

const linkStyle = () => css`
  &&& {
    &:hover,
    &:active {
      background-color: transparent;
      border-color: transparent;
      && a {
        text-decoration: underline;
      }
    }
  }
`;

const ctaStyle = ({ theme }) => css`
  &&& {
    &:hover,
    &:active {
      background-color: ${({ color, hoverBackgroundColor }) =>
        hoverBackgroundColor ? hoverBackgroundColor : color};
      border-color: ${({ color, hoverBorderColor }) =>
        hoverBorderColor ? hoverBorderColor : color};
      && a {
        color: ${({ backgroundColor, hoverTextColor }) =>
          hoverTextColor
            ? hoverTextColor
            : backgroundColor
            ? backgroundColor
            : theme.colors.primary};
      }
    }
  }
`;

const StyledButton = styled(Box)<StyledButtonProps>`
  ${(props) => basicStyle(props)}
  ${(props) => defaultStyle(props)}

  ${(props) => props.backgroundColor && backgroundStyle(props)}
  ${(props) => props.outline && outlineStyle(props)}
  ${(props) => props.ghost && ghostStyle(props)}
  ${(props) => props.isLink && linkStyle(props)}

  ${(props) => props.disabled && disabledStyle(props)}
  ${(props) => (props.color || props.fontWeight) && overrideStyle(props)}
  ${(props) => (props.isCTA ? ctaStyle(props) : null)}
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Button = forwardRef<any, typeof StyledButton>((props, ref) => {
  const _props = { ...props };
  const a_props = { ...props };
  delete _props.to;
  delete a_props.className;
  let target = props.target || "_self";
  if (target === "Same Tab") target = "_self";
  if (target === "New Tab") target = "_new";

  const anchorStyle = {
    textDecoration: "none",
    padding: "13px 18px",
    display: "block",
    textAlign: "center",
  };
  if (props.nopadding) delete anchorStyle.padding;

  return (
    <StyledButton ref={ref} {..._props} data-iscta={props.isCTA}>
      <a style={anchorStyle} href={props.to} target={target} {...a_props} />
    </StyledButton>
  );
});

export const Button2 = forwardRef<any, typeof StyledButton>((props, ref) => {
  const _props = { ...props };
  _props.textDecoration = "none !important";
  _props.padding = "13px 18px !important";
  _props.display = "block !important";
  _props.textAlign = "center !important";
  _props.color = "white !important";
  return (
    <StyledButton as="button" ref={ref} {..._props} data-iscta={props.isCTA} />
  );
});

Button.displayName = "Button";
Button2.displayName = "Button";
